import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatPercent } from "@/lib/utils";
import { ArrowRight, ArrowLeft } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import { API } from "@/lib/api";

function getGroup(name) {
  const lower = name.toLowerCase();
  if (lower.includes("preenchimento")) return "Preenchimento";
  if (lower.includes("bioestimulador")) return "Bioestimulador";
  if (lower.includes("botox") || lower.includes("toxina")) return "Botox";
  if (lower.includes("fio") || lower.includes("fios")) return "Fios";
  if (lower.includes("peeling")) return "Peeling";
  if (lower.includes("laser")) return "Laser";
  if (lower.includes("skinbooster")) return "Skinbooster";
  if (lower.includes("lipo") || lower.includes("enzima")) return "Lipo/Enzimas";
  return "Outros";
}

export default function Comparison() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/reports/comparison`);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching comparison:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  // Group data by category, sorted by margin within each group
  const grouped = {};
  data.forEach((item) => {
    const group = getGroup(item.name);
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(item);
  });

  // Sort each group by margin (best to worst)
  Object.keys(grouped).forEach((key) => {
    grouped[key].sort((a, b) => b.contribution_margin_percent - a.contribution_margin_percent);
  });

  // Sort groups by best avg margin
  const sortedGroups = Object.entries(grouped).sort((a, b) => {
    const avgA = a[1].reduce((s, i) => s + i.contribution_margin_percent, 0) / a[1].length;
    const avgB = b[1].reduce((s, i) => s + i.contribution_margin_percent, 0) / b[1].length;
    return avgB - avgA;
  });

  const chartData = data
    .sort((a, b) => b.contribution_margin_percent - a.contribution_margin_percent)
    .map(d => ({
      name: d.name.length > 15 ? d.name.slice(0, 15) + "..." : d.name,
      fullName: d.name,
      margin: d.contribution_margin_percent,
      marginValue: d.contribution_margin,
      profitable: d.is_profitable,
    }));

  return (
    <div className="space-y-6 animate-slide-in" data-testid="comparison-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/reports")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            Comparativo de Procedimentos
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Compare margens lado a lado — agrupado por categoria</p>
        </div>
      </div>

      {data.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-500">Nenhuma precificacao cadastrada</CardContent></Card>
      ) : (
        <>
          {/* Chart */}
          <Card>
            <CardHeader><CardTitle style={{ fontFamily: 'Manrope' }}>Margem de Contribuicao (%)</CardTitle></CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} />
                    <Tooltip
                      content={({ payload }) => {
                        if (!payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border text-sm">
                            <p className="font-medium">{d.fullName}</p>
                            <p className={d.profitable ? "text-emerald-600" : "text-rose-600"}>
                              Margem: {formatPercent(d.margin)} ({formatCurrency(d.marginValue)})
                            </p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="margin" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.profitable ? "#10b981" : "#ef4444"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Grouped Table */}
          <Card>
            <CardHeader><CardTitle style={{ fontFamily: 'Manrope' }}>Detalhamento por Grupo</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left p-3 font-medium">Procedimento</th>
                    <th className="text-right p-3 font-medium">Preco Venda</th>
                    <th className="text-right p-3 font-medium">Despesas</th>
                    <th className="text-right p-3 font-medium">Custo Unit.</th>
                    <th className="text-right p-3 font-medium">Margem R$</th>
                    <th className="text-right p-3 font-medium">Margem %</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGroups.map(([groupName, items]) => (
                    <>
                      <tr key={`group-${groupName}`} className="bg-slate-100 dark:bg-slate-800">
                        <td colSpan={6} className="p-3 font-bold text-slate-700 dark:text-slate-200 text-base">
                          {groupName}
                          <span className="ml-2 text-xs font-normal text-slate-500">({items.length} {items.length === 1 ? 'item' : 'itens'})</span>
                        </td>
                      </tr>
                      {items.map((item) => (
                        <tr key={item.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="p-3 pl-6 font-medium">{item.name}</td>
                          <td className="p-3 text-right font-mono">{formatCurrency(item.sale_price)}</td>
                          <td className="p-3 text-right font-mono">{formatCurrency(item.total_variable_expenses)}</td>
                          <td className="p-3 text-right font-mono">{formatCurrency(item.total_unit_cost)}</td>
                          <td className={`p-3 text-right font-mono font-bold ${item.is_profitable ? "text-emerald-600" : "text-rose-600"}`}>
                            {formatCurrency(item.contribution_margin)}
                          </td>
                          <td className={`p-3 text-right font-mono font-bold ${item.is_profitable ? "text-emerald-600" : "text-rose-600"}`}>
                            {formatPercent(item.contribution_margin_percent)}
                          </td>
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
